import React from "react"
import { lineSlug, LuasLine } from "./luas-api"
import * as styles from "./luas-stop-selector.module.scss"

type RenderProps = {
  lines: LuasLine[]
  lineName: string
  stopAbrev: string
  onLineChange: (lineName: string) => void
  onStopChange: (stopAbrev: string) => void
  error?: string | null
  labels?: { line: string; stop: string; selectLine: string; selectStop: string }
  legend?: string
  submitLabel?: string
  submitDisabled?: boolean
  buttonType?: "button" | "submit"
  buttonClassName?: string
  namedFields?: boolean
  hideSubmit?: boolean
  onSubmitClick?: () => void
}

const LuasStopSelector: React.FC<RenderProps> = ({
  lines,
  lineName,
  stopAbrev,
  onLineChange,
  onStopChange,
  error,
  labels = { line: "Line", stop: "Stop", selectLine: "- Select a line -", selectStop: "- Select a stop -" },
  legend = "Find a stop",
  submitLabel = "Find",
  submitDisabled,
  buttonType = "submit",
  buttonClassName,
  namedFields = false,
  hideSubmit = false,
  onSubmitClick,
}) => {
  const stopsForLine = lines.find(line => line.name === lineName)?.stops ?? []

  // Native form submission is kept as a no-JS fallback; when JS is active we handle it ourselves.
  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    if (!onSubmitClick) return
    event.preventDefault()
    onSubmitClick()
  }

  return (
    <form className={styles.form} onSubmit={handleSubmit}>
      {error && (
        <p className={styles.error} role="alert" aria-atomic="true">
          {error}
        </p>
      )}

      <fieldset className={styles.fieldset}>
        <legend className="sr-only">{legend}</legend>

        <label className={styles.field}>
          <span className={styles.fieldLabel}>{labels.line}</span>
          <select
            className={styles.select}
            name="line"
            value={namedFields ? lineSlug(lineName) : lineName}
            onChange={event => {
              const nextLine = namedFields
                ? lines.find(line => lineSlug(line.name) === event.target.value)
                : lines.find(line => line.name === event.target.value)
              onLineChange(nextLine?.name ?? "")
            }}
          >
            <option value="">{labels.selectLine}</option>
            {lines.map(line => (
              <option key={line.name} value={namedFields ? lineSlug(line.name) : line.name}>
                {line.name}
              </option>
            ))}
          </select>
        </label>

        <label className={styles.field}>
          <span className={styles.fieldLabel}>{labels.stop}</span>
          <select
            className={styles.select}
            name="stop"
            value={stopAbrev}
            onChange={event => onStopChange(event.target.value)}
            disabled={!lineName}
          >
            <option value="">{labels.selectStop}</option>
            {stopsForLine.map(stop => (
              <option key={stop.abrev} value={stop.abrev}>
                {stop.name}
              </option>
            ))}
          </select>
        </label>
      </fieldset>

      {!hideSubmit && (
        <button className={buttonClassName || styles.button} type={buttonType} disabled={submitDisabled || !stopAbrev}>
          {submitLabel}
        </button>
      )}
    </form>
  )
}

export default LuasStopSelector
